import type { UrlStaticSeoMeta } from './url-static-seo-meta';
import { STATIC_URL_SEO_META } from './url-static-seo-meta';

const IGNORED_SEARCH_PARAM_KEYS = new Set(['page', 'gclid', 'fbclid']);

function isIgnoredSearchParamKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (IGNORED_SEARCH_PARAM_KEYS.has(lower)) return true;
  if (lower.startsWith('utm_')) return true;
  return false;
}

/**
 * True when the URL carries refinements beyond tracking/pagination — in that case
 * generated SRP meta should win over the curated static map.
 */
export function hasSignificantSearchParams(searchParams: URLSearchParams): boolean {
  for (const key of searchParams.keys()) {
    if (isIgnoredSearchParamKey(key)) continue;
    const values = searchParams.getAll(key).filter((v) => v.trim() !== '');
    if (values.length > 0) return true;
  }
  return false;
}

/**
 * Normalizes to a STATIC_URL_SEO_META lookup key (no trailing slash; `/` and legacy `/en` → `/`).
 */
export function normalizePathnameForStaticSeoLookup(pathname: string): string {
  let p = pathname.trim();
  if (p.startsWith('http://') || p.startsWith('https://')) {
    try {
      p = new URL(p).pathname;
    } catch {
      p = '/';
    }
  }
  const noHashQuery = p.split('#')[0]?.split('?')[0] ?? '/';
  if (noHashQuery === '/' || noHashQuery === '' || noHashQuery === '/en') return '/';
  const noTrail = noHashQuery.replace(/\/+$/, '');
  if (noTrail === '/en') return '/';
  return noTrail || '/';
}

export function getStaticUrlSeoOverride(
  pathnameOnly: string,
  searchParams: URLSearchParams,
): UrlStaticSeoMeta | null {
  const key = normalizePathnameForStaticSeoLookup(pathnameOnly);
  const entry = STATIC_URL_SEO_META[key];
  if (!entry) return null;
  if (hasSignificantSearchParams(searchParams)) return null;
  return entry;
}
